import { lazy, Suspense, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiReachable } from '../../lib/api/connectivity.js';
import { AuthHero } from './AuthHero.js';
import { LanguageMenu } from './LanguageMenu.js';
import './auth.css';
import './layouts.css';

// Shown only while the API is unreachable, so loaded only then.
const BackSoonNotice = lazy(() =>
  import('./BackSoonNotice.js').then((module) => ({ default: module.BackSoonNotice })),
);

/**
 * The layout of the pages where someone signs in (sign-in, links opened from
 * email, "being set up"): the form on the right and, from 900 px, the brand
 * panel on the left; on a phone the panel becomes a band above the form.
 * The form comes first in the page's order, so keyboards and screen readers
 * reach it before the brand panel.
 *
 * @example
 * <AuthLayout><PageHeader title="Sign in" /></AuthLayout>
 *
 * `wide` is for a page with more to read than the sign-in form (the terms):
 * on a desktop the brand panel slides over to a slimmer column and the form
 * side gets the room.
 */
export function AuthLayout({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  const { t } = useTranslation();
  const reachable = useApiReachable();
  return (
    <div className={wide ? 'auth auth--wide' : 'auth'}>
      <a className="skip-link" href="#main">
        {t('shell.skipToContent')}
      </a>
      <div className="auth__panel">
        <div className="auth__bar">
          <LanguageMenu />
        </div>
        <main id="main" className="auth__main" tabIndex={-1}>
          {children}
        </main>
        <footer className="auth__footer">
          {t('shell.footer.copyright', { year: new Date().getFullYear() })}
        </footer>
      </div>
      <AuthHero />
      {reachable ? null : (
        <Suspense fallback={null}>
          <BackSoonNotice />
        </Suspense>
      )}
    </div>
  );
}
